import { SensitiveInfo, SensitiveInfoRelease } from "../../../models/transaction";
import { AUDIT } from "../../../models/requestType";

/**
 * AB#37984 (issue 1): Quick Entry's Audit default for the Sensitive Information questionnaire.
 *
 * Classic authorizes EVERY sensitive category as soon as the Request Type becomes Audit -
 * `DataEntryDetailsRightPane`'s `onChangeRequestType` maps the whole list to `checked: true` and
 * hands it to `applySensitiveInfoUpdate`, which PUTs it straight away. Quick Entry never got that
 * rule, so an Audit transaction opened with all five categories unanswered.
 *
 * ONE DELIBERATE DIFFERENCE FROM CLASSIC: this computes a PROPOSAL and persists nothing. Every
 * other Quick Entry row seeds local state and waits for the specialist to Confirm (see
 * DataEntryDetails.seedExtractedSensitiveInfo); an immediate PUT would break that contract for one
 * row only. The caller writes the result into `quickEntrySensitiveInfo` and leaves the row
 * requiring confirmation, so the observable outcome matches Classic without adopting its
 * persistence timing.
 *
 * AB#37984 (round-7 product decision, QA retest): the rule has NO OTHER exceptions. An earlier
 * revision stood this default down whenever the questionnaire already had an answer - the AI
 * declaration seed's (AB#37856) or a specialist's own manual entry - on the theory that discarding
 * a human-reviewed answer to re-apply a default was the wrong trade. QA's retest of the bug's own
 * literal expected result ("all sensitive categories should be auto-selected... matching legacy
 * behavior") made clear that was the wrong call: Classic has no such exception, so this doesn't
 * either. Audit ALWAYS authorizes all five, overwriting any prior answer.
 *
 * The trigger is Request Type = Audit ALONE. Nothing in the SPA keys on the "External Audit"
 * purpose id, so the rule fires for Internal Audit too - the bug report names External Audit only
 * because Audit offers no other purposes.
 *
 * @returns the proposed questionnaire, or `null` when no proposal applies. `null` means "leave
 *          state untouched AND do not burn the caller's latch".
 */
export function computeAuditSensitiveInfoDefaults(
    requestTypeId: number | undefined,
    sensitiveInfo: readonly SensitiveInfo[],
): SensitiveInfo[] | null {
    if (requestTypeId !== AUDIT) {
        return null;
    }
    // The questionnaire IS the write target; an empty list means it has not loaded yet, and
    // proposing one would lose the seed for good once the caller latches.
    if (sensitiveInfo.length === 0) {
        return null;
    }
    return sensitiveInfo.map(item => ({ ...item, stateID: SensitiveInfoRelease.Authorized }));
}
